import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TrendingUp, CreditCard, Eye, Mail, Briefcase, Download, Users } from 'lucide-react'
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import analyticsService from '@/services/analyticsService'
import { toast } from '@/stores/toastStore'
import { AnalyticsPeriod, AnalyticsDashboardData } from '@/types'
import { Link } from 'react-router-dom'
import PendingPaymentBanner from '@/components/recruiter/PendingPaymentBanner'

const CHART_COLORS = ['#3B82F6', '#6366F1', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981']

export default function RecruiterDashboard() {
  const { t } = useTranslation('recruiter')
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<AnalyticsDashboardData | null>(null)
  const [period, setPeriod] = useState<AnalyticsPeriod>('month')
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    loadAnalyticsData()
  }, [period])

  const loadAnalyticsData = async () => {
    try {
      setLoading(true)
      const analyticsData = await analyticsService.getDashboardOverview({ period })
      setData(analyticsData)
    } catch (error: any) {
      if (
        error.response?.status === 403 ||
        error.response?.data?.error?.includes('subscription') ||
        error.response?.data?.error?.includes('pack')
      ) {
        setData(null)
      } else {
        toast.error(error.response?.data?.error || t('analytics.errorLoading'))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleExport = async (type: 'usage' | 'hiring-performance') => {
    try {
      setExporting(true)
      await analyticsService.downloadExport({ type })
      toast.success(t('analytics.export.success'))
    } catch (error: any) {
      toast.error(t('analytics.export.error'))
    } finally {
      setExporting(false)
    }
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num)
  }

  if (loading) {
    return (
      <div className="container-custom py-12">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="container-custom py-12">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-12 shadow-sm">
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <TrendingUp className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-3">
              {t('analytics.emptyStates.noSubscription.title')}
            </h2>
            <p className="text-gray-500 mb-6">
              {t('analytics.emptyStates.noSubscription.description')}
            </p>
            <Link
              to="/recruiter/packs"
              className="inline-block px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              {t('analytics.emptyStates.noSubscription.action')}
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const { usage, hiringPerformance, insights } = data

  const usageData = usage.usageByType.map((item, index) => ({
    name: t(`analytics.usage.types.${item.type}`),
    count: item.count,
    color: CHART_COLORS[index % CHART_COLORS.length],
  }))

  const funnelMax = hiringPerformance.conversionFunnel.views || 1
  const funnelStages = [
    {
      label: t('analytics.charts.stages.views'),
      value: hiringPerformance.conversionFunnel.views,
      pct: 100,
      color: 'bg-blue-500',
    },
    {
      label: t('analytics.charts.stages.saves'),
      value: hiringPerformance.conversionFunnel.saves,
      pct: Math.round((hiringPerformance.conversionFunnel.saves / funnelMax) * 100),
      color: 'bg-violet-500',
    },
    {
      label: t('analytics.charts.stages.contacts'),
      value: hiringPerformance.conversionFunnel.contactReveals,
      pct: Math.round((hiringPerformance.conversionFunnel.contactReveals / funnelMax) * 100),
      color: 'bg-emerald-500',
    },
  ]

  const kpis = [
    {
      icon: <CreditCard className="w-4 h-4 text-blue-600" />,
      bg: 'bg-blue-50',
      label: t('analytics.kpis.aiMatchesRemaining'),
      value: formatNumber(insights.aiMatchesRemaining),
    },
    {
      icon: <Eye className="w-4 h-4 text-violet-600" />,
      bg: 'bg-violet-50',
      label: t('analytics.kpis.profileViews'),
      value: formatNumber(insights.totalProfileViews),
    },
    {
      icon: <Mail className="w-4 h-4 text-emerald-600" />,
      bg: 'bg-emerald-50',
      label: t('analytics.kpis.contactReveals'),
      value: formatNumber(insights.totalContactReveals),
    },
    {
      icon: <Briefcase className="w-4 h-4 text-orange-600" />,
      bg: 'bg-orange-50',
      label: t('analytics.kpis.activeJobs'),
      value: formatNumber(insights.activeJobs),
    },
    {
      icon: <Users className="w-4 h-4 text-pink-600" />,
      bg: 'bg-pink-50',
      label: t('analytics.kpis.savedCandidates'),
      value: formatNumber(insights.totalSavedCandidates),
    },
  ]

  return (
    <div className="container-custom py-8">
      {/* Pending payment banner */}
      <PendingPaymentBanner />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('analytics.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('analytics.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as AnalyticsPeriod)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="week">{t('analytics.periods.week')}</option>
            <option value="month">{t('analytics.periods.month')}</option>
            <option value="quarter">{t('analytics.periods.quarter')}</option>
            <option value="year">{t('analytics.periods.year')}</option>
          </select>
          <button
            onClick={() => handleExport('usage')}
            disabled={exporting}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Download className="w-4 h-4" />
            {exporting ? t('analytics.export.downloading') : t('analytics.export.button')}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        {kpis.map((kpi) => (
          <div
            key={kpi.label}
            className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-5 shadow-sm"
          >
            <div className={`w-8 h-8 ${kpi.bg} rounded-lg flex items-center justify-center mb-3`}>
              {kpi.icon}
            </div>
            <p className="text-2xl font-bold text-gray-900 leading-none mb-1">{kpi.value}</p>
            <p className="text-xs text-gray-500">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Pack Quota */}
      <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 shadow-sm mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-700">
            {t('analytics.usage.quota.title')}
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {/* AI Searches */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm text-gray-600">{t('analytics.usage.types.AI_MATCH')}</span>
              <span className="text-sm font-semibold text-gray-900">
                {t('analytics.usage.quota.usedOf', {
                  used: usage.aiMatches.used,
                  allowed: usage.aiMatches.allowed,
                })}
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2 mb-1.5">
              <div
                className={`h-2 rounded-full transition-all duration-500 ${
                  usage.aiMatches.utilizationPercentage >= 85 ? 'bg-red-500' : 'bg-blue-500'
                }`}
                style={{ width: `${Math.min(usage.aiMatches.utilizationPercentage, 100)}%` }}
              />
            </div>
            <div className="flex items-center justify-end">
              <span
                className={`text-xs font-medium ${
                  usage.aiMatches.utilizationPercentage >= 85 ? 'text-red-600' : 'text-gray-400'
                }`}
              >
                {usage.aiMatches.utilizationPercentage}%
              </span>
            </div>
            {usage.aiMatches.utilizationPercentage >= 85 && (
              <p className="text-xs text-red-600 mt-1">{t('analytics.usage.quota.willExhaust')}</p>
            )}
          </div>

          {/* Contact Reveals */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm text-gray-600">
                {t('analytics.usage.types.CONTACT_REVEAL')}
              </span>
              <span className="text-sm font-semibold text-gray-900">
                {t('analytics.usage.quota.usedOf', {
                  used: usage.contactReveals.used,
                  allowed: usage.contactReveals.allowed,
                })}
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2 mb-1.5">
              <div
                className={`h-2 rounded-full transition-all duration-500 ${
                  usage.contactReveals.utilizationPercentage >= 85 ? 'bg-red-500' : 'bg-emerald-500'
                }`}
                style={{ width: `${Math.min(usage.contactReveals.utilizationPercentage, 100)}%` }}
              />
            </div>
            <div className="flex items-center justify-end">
              <span
                className={`text-xs font-medium ${
                  usage.contactReveals.utilizationPercentage >= 85
                    ? 'text-red-600'
                    : 'text-gray-400'
                }`}
              >
                {usage.contactReveals.utilizationPercentage}%
              </span>
            </div>
            {usage.contactReveals.utilizationPercentage >= 85 && (
              <p className="text-xs text-red-600 mt-1">{t('analytics.usage.quota.willExhaust')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Credit Usage by Type */}
        <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">
            {t('analytics.charts.usageByType')}
          </h2>
          {usageData.length === 0 ? (
            <div className="flex items-center justify-center h-[180px] text-sm text-gray-400">
              {t('analytics.charts.noDataAvailable')}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={usageData} barSize={28}>
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: '#9CA3AF' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  cursor={{ fill: '#F9FAFB' }}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: '0px',
                    fontSize: 12,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  }}
                  formatter={(value: number) => [formatNumber(value), t('analytics.usage.title')]}
                />
                <Bar dataKey="count" radius={[0, 0, 0, 0]}>
                  {usageData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Hiring Funnel */}
        <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-700 mb-5">
            {t('analytics.charts.hiringFunnel')}
          </h2>
          <div className="space-y-5">
            {funnelStages.map((stage) => (
              <div key={stage.label}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-gray-600">{stage.label}</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {formatNumber(stage.value)}
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className={`${stage.color} h-2 rounded-full transition-all duration-500`}
                    style={{ width: `${stage.pct}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Efficiency Metrics */}
      <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-700 mb-5">
          {t('analytics.insights.title')}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-xs text-gray-500 mb-1">{t('analytics.insights.viewsPerContact')}</p>
            <p className="text-xl font-bold text-gray-900">{insights.viewsPerContact.toFixed(1)}</p>
            <p className="text-xs text-gray-400 mt-0.5">{t('analytics.insights.lowerIsBetter')}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">
              {t('analytics.insights.contactSuccessRate')}
            </p>
            <p className="text-xl font-bold text-gray-900">
              {insights.contactSuccessRate.toFixed(1)}%
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">{t('analytics.insights.saveRate')}</p>
            <p className="text-xl font-bold text-gray-900">{insights.saveRate.toFixed(1)}%</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">{t('analytics.insights.viewsPerJob')}</p>
            <p className="text-xl font-bold text-gray-900">{insights.viewsPerJob.toFixed(1)}</p>
          </div>
        </div>
      </div>
    </div>
  )
}
